// View 4 — Manage roles (permission matrix).
//
// Per design/UI : UX v1.0/for-engineering-users/specs/users-strings.md §View 4.
// Wraps the design-system PermissionMatrix and supplies localized labels +
// per-role user counts (sourced from /users → counts grouped client-side).

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, PermissionMatrix, Spinner } from '@gc-coach/design-system';
import {
  ALL_PERMISSIONS,
  ALL_ROLES,
  type PermissionGroup,
  type RoleKey,
} from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useI18n } from '../i18n/index.js';
import { roleLabel } from '../shell/roleLabels.js';
import { useUsers } from './useUsers.js';
import styles from './ManageRolesPage.module.css';

export function ManageRolesPage() {
  const navigate = useNavigate();
  const { t, tt } = useI18n();
  const { data, isLoading, errorCode } = useUsers({});

  const userCountByRole = useMemo<Record<RoleKey, number>>(() => {
    const counts: Record<RoleKey, number> = {
      super_admin: 0,
      content_editor: 0,
      customer_success: 0,
      analyst: 0,
      read_only: 0,
    };
    if (data) {
      for (const u of data.users) {
        for (const r of u.roles) counts[r] += 1;
      }
    }
    return counts;
  }, [data]);

  const roleColumns = useMemo(
    () =>
      ALL_ROLES.map((role) => ({
        role,
        label: roleLabel(role, t),
        userCount: userCountByRole[role],
        meta: tt('users.rolesColUsersTemplate', { n: userCountByRole[role] }),
      })),
    [userCountByRole, t, tt],
  );

  const permissionRows = useMemo(
    () =>
      ALL_PERMISSIONS.map((permission) => ({
        permission,
        label: t(`permissionLabels.${permission}`),
      })),
    [t],
  );

  const groupLabels: Record<PermissionGroup, string> = {
    knowledge: t('permissionGroups.knowledge'),
    insights: t('permissionGroups.insights'),
    customers: t('permissionGroups.customers'),
    admin: t('permissionGroups.admin'),
  };

  return (
    <AdminShell
      activeKey="users"
      breadcrumb={[t('users.breadcrumbTeam'), t('users.breadcrumbUsers'), t('users.breadcrumbRoles')]}
      state={isLoading && !data ? { kind: 'loading' } : { kind: 'normal' }}
    >
      <PageHeader
        title={t('users.rolesTitle')}
        sub={t('users.rolesSub')}
        actions={
          <Button kind="ghost" onClick={() => navigate('/admin/team/users')}>
            {t('users.rolesBackButton')}
          </Button>
        }
      />

      <div className={styles.callout}>
        <Alert severity="info">{t('users.rolesCallout')}</Alert>
      </div>

      {errorCode && (
        <div className={styles.callout}>
          <Alert severity="danger">{t('users.detailGenericError')}</Alert>
        </div>
      )}

      {isLoading && !data ? (
        <div className={styles.loadingState}>
          <Spinner />
        </div>
      ) : (
        <PermissionMatrix
          roleColumns={roleColumns}
          permissions={permissionRows}
          groupLabels={groupLabels}
        />
      )}
    </AdminShell>
  );
}
